import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { DeleteIcon, UserCheckIcon } from '@repo/ui/icons'

import { UseDataManagerUpdateFields } from '../../../../lib/hooks/use-data-manager'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { MissionReportStatusEnum } from '../../../../lib/types/enums/mission/mission-report-status.enum'
import {
  MissionStaffStatusEnum,
  missionStaffStatusEnumMapping
} from '../../../../lib/types/enums/mission/mission-staff-status.enum'
import { MissionTypeEnum } from '../../../../lib/types/enums/mission/mission-type.enum'
import { StaffQualificationEnum } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { Mission } from '../../../../lib/types/models/mission/mission'
import { BudgetMissionInput } from '../../../../lib/types/models/mission/mission-input'
import { MissionToExternalStaff } from '../../../../lib/types/models/mission-to-external-staff/mission-to-external-staff'
import { MissionToLecStaff } from '../../../../lib/types/models/mission-to-lec-staff/mission-to-lec-staff'
import { MissionToStaff } from '../../../../lib/types/models/mission-to-staff/mission-to-staff'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import CheckboxStatefulInput from '../../../elements/inputs/checkbox-stateful-input'
import CurrencyInput from '../../../elements/inputs/currency-input'
import { QualificationTags } from '../../../elements/qualification-badge'
import MissionToStaffReportBadge from '../../mission-trackings/mission-to-staff-report-badge'

export type AffectedUser = {
  id: number
  firstName: string
  lastName: string
  email?: string
  phone?: string
  birthDate?: string

  budgetCurrency?: CurrencyEnum
  budget?: number
  budgetHandled?: boolean

  status: MissionStaffStatusEnum

  qualifications: StaffQualificationEnum[]
  missionCount?: number
  ticketingRequired?: boolean
  ticketingHandled?: boolean
  hotelRequired?: boolean
  hotelHandled?: boolean

  missionToStaffId?: number
  reportStatus?: MissionReportStatusEnum
}

export type AffectedUserType = 'EXTERNAL' | 'LEC' | 'STAFF'

export type ActionParams = {
  missionId: number
  id: number
}

export type UpdateBudgetActionParams = Pick<ActionParams, 'id'> & BudgetMissionInput

export const affectedListColumns = (
  missionId: number,
  mode: 'view' | 'edit',
  actions: {
    assignTicket?: (params: ActionParams) => Promise<void>
    unassignTicket?: (params: ActionParams) => Promise<void>

    assignHotel?: (params: ActionParams) => Promise<void>
    unassignHotel?: (params: ActionParams) => Promise<void>

    includeBudget?: (params: ActionParams) => Promise<void>
    excludeBudget?: (params: ActionParams) => Promise<void>

    updateBudget?: (params: UpdateBudgetActionParams) => Promise<void>

    updateFields?: UseDataManagerUpdateFields<AffectedUser>

    onConfirm: (data: AffectedUser) => void
    onCancel: (data: AffectedUser) => void
  }
): (Column<AffectedUser> & { version: AffectedUserType[]; visible?: boolean })[] => {
  return [
    {
      version: ['EXTERNAL', 'STAFF', 'LEC'],
      header: 'NOM',
      accessorKey: 'lastName',
      cell: ({ row }) => row.original.lastName.toUpperCase()
    },
    {
      version: ['EXTERNAL', 'STAFF', 'LEC'],
      header: 'Prénom',
      accessorKey: 'firstName'
    },
    {
      version: ['STAFF'],
      header: 'Qualifications',
      accessorKey: 'qualifications',
      cell: ({ row }) => <QualificationTags qualifications={row.original.qualifications} />
    },
    {
      version: ['STAFF'],
      header: '# missions identiques',
      accessorKey: 'missionCount',
      cell: ({ row }) => (row.original.missionCount ?? 0) - 1
    },
    {
      version: ['EXTERNAL'],
      header: 'Email',
      accessorKey: 'email'
    },
    {
      version: ['EXTERNAL'],
      header: 'Téléphone',
      accessorKey: 'phone',
      cell: ({ row }) => (row.original.phone ? formatPhoneNumber(row.original.phone) : '-')
    },
    {
      version: ['STAFF'],
      header: 'Billetterie?',
      accessorKey: 'ticketingRequired',
      cell: ({ row }) => {
        const missionToStaff = row.original
        if (!missionToStaff.ticketingRequired) return 'NA'
        return (
          <CheckboxStatefulInput
            value={missionToStaff.ticketingHandled ?? false}
            onChange={async (value) => {
              actions.updateFields?.(row.index, [{ key: 'ticketingHandled', value }])
              if (!value) await actions.unassignTicket?.({ missionId, id: missionToStaff.id })
              else await actions.assignTicket?.({ missionId, id: missionToStaff.id })
            }}
          />
        )
      }
    },
    {
      version: ['STAFF'],
      header: 'Hotel?',
      accessorKey: 'hotelRequired',
      cell: ({ row }) => {
        const missionToStaff = row.original
        if (!missionToStaff.hotelRequired) return 'NA'
        return (
          <CheckboxStatefulInput
            value={missionToStaff.hotelHandled ?? false}
            onChange={async (value) => {
              actions.updateFields?.(row.index, [{ key: 'hotelHandled', value }])
              if (!value) await actions.unassignHotel?.({ missionId, id: missionToStaff.id })
              else await actions.assignHotel?.({ missionId, id: missionToStaff.id })
            }}
          />
        )
      }
    },
    {
      version: ['EXTERNAL', 'STAFF', 'LEC'],
      header: 'Budget remis',
      accessorKey: '',
      cell: ({ row }) => (
        <CurrencyInput
          value={{
            selectedOptionValue: row.original.budgetCurrency,
            value: row.original.budget?.toString()
          }}
          onChange={async (value) => {
            if (value.selectedOptionValue) {
              const amount = value.value ? +value.value : null
              actions.updateFields?.(row.index, [
                { key: 'budget', value: amount ?? undefined },
                { key: 'budgetCurrency', value: value.selectedOptionValue },
                {
                  key: 'budgetHandled',
                  value: amount && amount > 0 ? row.original.budgetHandled : false
                }
              ])
              // Check if the value becomes 0, if so, remove the budget handled
              if (!amount || amount === 0)
                await actions.excludeBudget?.({ missionId, id: row.original.id })

              await actions.updateBudget?.({
                id: row.original.id,
                amount,
                currency: value.selectedOptionValue
              })
            }
          }}
        />
      )
    },
    {
      version: ['EXTERNAL', 'STAFF', 'LEC'],
      header: 'Budget rendu',
      accessorKey: 'budgetHandled',
      cell: ({ row }) => (
        <CheckboxStatefulInput
          disabled={!row.original.budget || row.original.budget === 0}
          value={!!row.original.budgetHandled}
          onChange={async (value) => {
            actions.updateFields?.(row.index, [{ key: 'budgetHandled', value }])
            const item = row.original
            if (value) await actions.includeBudget?.({ missionId, id: item.id })
            else await actions.excludeBudget?.({ missionId, id: item.id })
          }}
        />
      )
    },
    {
      version: ['EXTERNAL', 'STAFF', 'LEC'],
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => (
        <Badge
          text={missionStaffStatusEnumMapping[row.original.status].label}
          variant={missionStaffStatusEnumMapping[row.original.status].variant}
        />
      )
    },
    {
      version: ['STAFF'],
      enableSorting: false,
      header: 'Rapport',
      exportable: false,
      accessorKey: 'reportStatus',
      cell: ({ row }) =>
        row.original.missionToStaffId && row.original.reportStatus ? (
          <MissionToStaffReportBadge
            missionToStaffId={row.original.missionToStaffId}
            reportStatus={row.original.reportStatus}
          />
        ) : null
    },
    {
      visible: mode === 'edit',
      version: ['EXTERNAL', 'STAFF', 'LEC'] as AffectedUserType[],
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={row.original.status === MissionStaffStatusEnum.AFFECTED ? 'grey' : 'primary'}
            icon={UserCheckIcon}
            disabled={row.original.status === MissionStaffStatusEnum.AFFECTED}
            onClick={() => actions.onConfirm(row.original)}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => actions.onCancel(row.original)}
          />
        </div>
      )
    }
  ]
}

export const mapExternalStaffToAffectedUser = (user: MissionToExternalStaff): AffectedUser => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  phone: user.phone,
  qualifications: [],
  status: user.status,

  budgetHandled: user.budgetHandled,
  budget: user.budget,
  budgetCurrency: user.budgetCurrency
})

export const mapLecStaffToAffectedUser = (user: MissionToLecStaff): AffectedUser => ({
  id: user.userId,
  firstName: user.user.firstName,
  lastName: user.user.lastName,
  qualifications: [],
  status: user.status,

  budgetHandled: user.budgetHandled,
  budget: user.budget,
  budgetCurrency: user.budgetCurrency
})

export const mapStaffToAffectedUser = (user: MissionToStaff): AffectedUser => ({
  id: user.staffId,
  firstName: user.staff.firstName,
  lastName: user.staff.lastName,
  qualifications: user.staff.qualifications,
  status: user.status,
  birthDate: user.staff.birthDate,

  missionCount: user.missionCount,
  missionToStaffId: user.id,
  reportStatus: user.reportStatus,

  ticketingRequired: user.mission?.ticketingRequired,
  ticketingHandled: user.ticketingHandled,
  hotelRequired: user.mission?.hotelRequired,
  hotelHandled: user.hotelHandled,

  budgetHandled: user.budgetHandled,
  budget: user.budget,
  budgetCurrency: user.budgetCurrency
})

export const mapUserToAffectedUser = (
  type: AffectedUserType,
  user: MissionToStaff | MissionToLecStaff | MissionToExternalStaff
): AffectedUser => {
  switch (type) {
    case 'EXTERNAL':
      return mapExternalStaffToAffectedUser(user as MissionToExternalStaff)
    case 'LEC':
      return mapLecStaffToAffectedUser(user as MissionToLecStaff)
    case 'STAFF':
      return mapStaffToAffectedUser(user as MissionToStaff)
  }
}

export const getAffectedUserBasedOnType = (mission: Mission): AffectedUser[] => {
  if (mission.type === MissionTypeEnum.EXTERNAL)
    return mission.affectedExternalStaffs.map(mapExternalStaffToAffectedUser)
  if (mission.type === MissionTypeEnum.OFFICE)
    return mission.affectedLecStaffs.map(mapLecStaffToAffectedUser)
  return mission.affectedStaffs.map(mapStaffToAffectedUser)
}
